import { ApiCacheTags, ApiCacheTagsArray } from '@qapline/api';
import { attachmentsApi as sharedAttachmentsApi } from '@qapline/core';
import { timestamp } from '@qapline/helpers';
import type { TagDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { config } from 'config';
import { attachmentsApi } from 'core/attachments/api';

import { TAttachmentUpdateRequestBody, TAttachmentUpdateResponseBody } from '../types';

const updateAttachmentApi = attachmentsApi.injectEndpoints({
    endpoints: (builder) => ({
        updateAttachment: builder.mutation<TAttachmentUpdateResponseBody, TAttachmentUpdateRequestBody>({
            query: ({ attachmentId, ...rest }) => {
                return {
                    url: config.api.endpoints.attachments.update.replace(':id', attachmentId.toString()),
                    method: 'PUT',
                    body: {
                        ...rest,
                    },
                };
            },
            onQueryStarted: async (
                { attachmentId, dueDate, name, paymentDate, sent, type },
                { dispatch, queryFulfilled, getState },
            ) => {
                const queries = sharedAttachmentsApi.util.selectInvalidatedBy(getState(), [
                    ApiCacheTags.attachment.item,
                ]);

                const patches: { undo: () => void }[] = [];
                for (const { endpointName, originalArgs } of queries) {
                    if (endpointName !== 'getAttachment') continue;

                    const patch = dispatch(
                        sharedAttachmentsApi.util.updateQueryData('getAttachment', originalArgs, (draft) => {
                            if (draft.attachment_id !== attachmentId) return;
                            sent !== undefined && (draft.sent = sent);
                            dueDate !== undefined && (draft.dueDate = dueDate === null ? null : timestamp(dueDate));
                            paymentDate !== undefined &&
                                (draft.paymentDate = paymentDate === null ? null : timestamp(paymentDate));
                            name !== undefined && (draft.name = name);
                            type !== undefined && (draft.type = type);
                        }),
                    );
                    patches.push(patch);
                }

                try {
                    await queryFulfilled;
                } catch (error) {
                    patches.forEach((patch) => patch.undo());
                }
            },
            invalidatesTags: (response, error, { invoiceId, commissionId }) => {
                const tags: TagDescription<(typeof ApiCacheTagsArray)[number]>[] = [];
                if (error) return [];
                const relatedCommissionId = commissionId ?? response?.commission_id;
                if (invoiceId || relatedCommissionId) {
                    tags.push({ type: ApiCacheTags.attachment.item });
                }
                if (relatedCommissionId) {
                    tags.push({ type: ApiCacheTags.commission.item, id: relatedCommissionId });
                }
                tags.push({ type: ApiCacheTags.attachment.list }, { type: ApiCacheTags.commission.list });
                return tags;
            },
        }),
    }),
});

export const { useUpdateAttachmentMutation } = updateAttachmentApi;
